import { api } from '@/lib/axios'
import type { ExportRequest } from '@/lib/export-request'
import type {
  DashboardAntwort,
  DocumentLayoutConfig,
  DocumentLineResponseView,
  DocumentResponseHead,
  DocumentResponseKind,
  DocumentResponseView,
  PositionDraft,
} from '@symphenia/shared-types'
import { mitStand } from '@/lib/speichern'
import type {
  ArticleRef,
  BillingStatus,
  ClerkRef,
  ClipboardItem,
  ContactRef,
  UnitRef,
  DocumentDetail,
  DocumentHeaderPayload,
  DocumentLayoutRow,
  DocumentListRow,
  DocumentSettingsData,
  KindSettingPayload,
  AgingBucket,
  DunningProposal,
  SendDraft,
  DeductionCandidate,
  KindInfo,
  LineInput,
  PastePreview,
  ListDocumentsParams,
  Paginated,
  PartnerAddressRef,
  PartnerRef,
  PartnerSnapshot,
  ProjectPositionRef,
  ProjectRef,
} from '../types/document.types'

type Einzel<T> = { data: T }

export const documentsApi = {
  list: (params?: ListDocumentsParams) => api.get<Paginated<DocumentListRow>>('/documents', { params }),
  /** AP-40 7.3 — alle Kacheln des Dashboards in einem Aufruf. */
  dashboard: (scope: 'all' | 'mine') => api.get<Einzel<DashboardAntwort>>('/documents/dashboard', { params: { scope } }).then((r) => r.data.data),
  get: (id: string) => api.get<Einzel<DocumentDetail>>(`/documents/${id}`),
  kinds: () => api.get<Einzel<KindInfo[]>>('/documents/kinds'),

  /** N-105: Die Maske öffnet den neuen Beleg **vorläufig** — angelegt ist er erst mit „Speichern" (`keep`). */
  create: (payload: DocumentHeaderPayload & { kind: string }) => api.post<Einzel<DocumentDetail>>('/documents', payload, { params: { provisional: true } }),
  keep: (id: string) => api.post<Einzel<DocumentDetail>>(`/documents/${id}/keep`),
  discard: (id: string) => api.delete<{ message: string }>(`/documents/${id}/provisional`),
  update: (id: string, payload: DocumentHeaderPayload) => api.patch<Einzel<DocumentDetail>>(`/documents/${id}`, payload),
  remove: (id: string) => api.delete<{ message: string }>(`/documents/${id}`),

  setLines: (id: string, lines: LineInput[]) => api.put<Einzel<DocumentDetail>>(`/documents/${id}/lines`, { lines }),
  fromArticles: (id: string, payload: { articles: { articleId: string; quantity?: number }[]; afterLineId?: string | null; includeImage?: boolean }) =>
    api.post<Einzel<DocumentDetail>>(`/documents/${id}/lines/from-articles`, payload),
  fromProject: (id: string, payload: { projectPositionIds: string[]; afterLineId?: string | null }) =>
    api.post<Einzel<DocumentDetail>>(`/documents/${id}/lines/from-project`, payload),
  /** N-103: Was Einfügen ergäbe — schreibt nichts; gespeichert wird mit `setLines` (`clipboard` je Zeile). */
  pastePreview: (id: string, payload: { itemIds: string[]; refreshPrices?: boolean }) =>
    api.post<Einzel<PastePreview>>(`/documents/${id}/lines/paste-preview`, payload),
  paste: (id: string, payload: { itemIds: string[]; afterLineId?: string | null; refreshPrices?: boolean }) =>
    api.post<Einzel<DocumentDetail> & { hints: string[] }>(`/documents/${id}/lines/paste`, payload),

  uploadImage: (id: string, lineId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<Einzel<DocumentDetail>>(`/documents/${id}/lines/${lineId}/image`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  deleteImage: (id: string, lineId: string) => api.delete<Einzel<DocumentDetail>>(`/documents/${id}/lines/${lineId}/image`),
  /** Nachtrag 4: Dateien an Positionen — gehen beim Mailversand als Anhang mit. */
  uploadLineFile: (id: string, lineId: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<Einzel<DocumentDetail>>(`/documents/${id}/lines/${lineId}/files`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  deleteLineFile: (id: string, lineId: string, fileId: string) =>
    api.delete<Einzel<DocumentDetail>>(`/documents/${id}/lines/${lineId}/files/${fileId}`),
  lineFile: (id: string, lineId: string, fileId: string) =>
    api.get<ArrayBuffer>(`/documents/${id}/lines/${lineId}/files/${fileId}`, { responseType: 'arraybuffer' }),
  /** Nachtrag 6: Lieferschein — Freigabe, händischer Verrechnungsstand, unterschriebenes PDF. */
  setBilling: (id: string, payload: { released?: boolean; manualStatus?: BillingStatus | null }) =>
    api.patch<Einzel<DocumentDetail>>(`/documents/${id}/billing`, payload),
  uploadSignedPdf: (id: string, file: File) => {
    const form = new FormData()
    form.append('file', file)
    return api.post<Einzel<DocumentDetail>>(`/documents/${id}/signed-pdf`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  },
  signedPdf: (id: string) => api.get<ArrayBuffer>(`/documents/${id}/signed-pdf`, { responseType: 'arraybuffer' }),
  deleteSignedPdf: (id: string) => api.delete<Einzel<DocumentDetail>>(`/documents/${id}/signed-pdf`),
  /** N-4: das Foto als Bytes mit dem Anmelde-Token — keine vorsignierte Speicher-Adresse. */
  lineImage: (id: string, lineId: string) => api.get<Blob>(`/documents/${id}/lines/${lineId}/image`, { responseType: 'blob' }),

  /** AP-35 Phase H: „Projekt aus Beleg" — der Endpunkt gehört dem Projektmanagement, hier nur der Aufruf. */
  projectFromDocument: (documentId: string) => api.post<Einzel<{ projectId: string }>>('/projects/from-document', { documentId }),
  /** AP-39 Nachtrag 2 (5.2): `hints` — was die Übernahme zu sagen hat (Preise aus dem Angebot, Weggelassenes …). */
  copy: (id: string, payload: { targetKind: string; lineIds?: string[]; percent?: number }) =>
    api.post<Einzel<DocumentDetail> & { hints?: string[] }>(`/documents/${id}/copy`, payload, { params: { provisional: true } }),
  collectInvoice: (deliveryNoteIds: string[]) =>
    api.post<Einzel<DocumentDetail>>('/documents/collect-invoice', { deliveryNoteIds }, { params: { provisional: true } }),

  // ── Zwischenablage (5.8) ──
  clipboard: () => api.get<Einzel<ClipboardItem[]>>('/documents/clipboard'),
  copyToClipboard: (documentId: string, lineIds: string[]) =>
    api.post<Einzel<ClipboardItem[]>>('/documents/clipboard/from-document', { documentId, lineIds }),
  addToClipboard: (items: { sourceKind: string; sourceLabel?: string; position: PositionDraft }[]) =>
    api.post<Einzel<ClipboardItem[]>>('/documents/clipboard', { items }),
  removeClipboardItem: (itemId: string) => api.delete<Einzel<ClipboardItem[]>>(`/documents/clipboard/${itemId}`),
  clearClipboard: () => api.delete<Einzel<ClipboardItem[]>>('/documents/clipboard'),

  // ── Festschreiben, Storno, Gutschrift (7.5, 8.3) ──
  post: (id: string) => api.post<Einzel<DocumentDetail>>(`/documents/${id}/post`),
  cancel: (id: string) => api.post<Einzel<DocumentDetail>>(`/documents/${id}/cancel`),
  creditNote: (id: string) => api.post<Einzel<DocumentDetail>>(`/documents/${id}/credit-note`, undefined, { params: { provisional: true } }),

  // ── Zahlungen, Offene Posten, Mahnwesen (8.4, 8.5) ──
  addPayment: (id: string, payload: { paidOn: string; amount: number; method?: string; isDiscount?: boolean; note?: string }) =>
    api.post<Einzel<DocumentDetail>>(`/documents/${id}/payments`, payload),
  removePayment: (id: string, paymentId: string) => api.delete<Einzel<DocumentDetail>>(`/documents/${id}/payments/${paymentId}`),
  aging: () => api.get<Einzel<AgingBucket[]>>('/documents/open-items/aging'),
  dunningProposal: (payload: { clientId?: string } = {}) => api.post<Einzel<DunningProposal[]>>('/documents/dunning/proposal', payload),
  createDunning: (invoiceIds: string[]) => api.post<Einzel<DocumentDetail>>('/documents/dunning', { invoiceIds }, { params: { provisional: true } }),

  // ── Listen-Export (12.) — als Anfrage für den ExportButton (AP-10 Nachtrag 5) ──
  exportRequest: (
    params: ListDocumentsParams & {
      format: 'xlsx' | 'docx' | 'pdf'
      orientation?: 'portrait' | 'landscape'
      columns: { key: string; label: string }[]
      filterInfo?: string
    },
  ): ExportRequest => {
    const { columns, ...rest } = params
    return { url: '/documents/export', params: { ...rest, columns: JSON.stringify(columns) } }
  },

  // ── Mailversand (14.) ──
  sendDraft: (id: string) => api.get<Einzel<SendDraft>>(`/documents/${id}/send`),
  send: (
    id: string,
    // AP-10 Nachtrag 1: `transport` — ohne Angabe gilt die Vorwahl des Mandanten.
    payload: { to: string[]; cc?: string[]; copyToSelf?: boolean; subject: string; message: string; transport?: 'MICROSOFT_365' | 'SYSTEM' },
    // Nachtrag 10: eigene Dateien — dann als Formular (`mail` als JSON, Dateien in `files`), sonst JSON wie bisher.
    dateien: readonly File[] = [],
  ) => {
    if (!dateien.length) return api.post<Einzel<DocumentDetail>>(`/documents/${id}/send`, payload)
    const formular = new FormData()
    formular.append('mail', JSON.stringify(payload))
    for (const d of dateien) formular.append('files', d, d.name)
    return api.post<Einzel<DocumentDetail>>(`/documents/${id}/send`, formular)
  },
  /** AP-10 Nachtrag 2: einen verschickten Downloadlink zurückholen. */
  revokeLink: (id: string, linkId: string) => api.post<Einzel<DocumentDetail>>(`/documents/${id}/links/${linkId}/revoke`),

  // ── Teil- und Schlussrechnung (Phase I) ──
  deductionCandidates: (id: string) => api.get<Einzel<DeductionCandidate[]>>(`/documents/${id}/deductions`),
  addDeductions: (id: string, invoiceIds: string[]) => api.post<Einzel<DocumentDetail>>(`/documents/${id}/deductions`, { invoiceIds }),

  // ── PDF (11.2) ──
  pdf: (id: string) => api.get<ArrayBuffer>(`/documents/${id}/pdf`, { responseType: 'arraybuffer' }),

  // ── Einrichtung (13.4) ──
  settings: () => api.get<Einzel<DocumentSettingsData>>('/documents/settings'),
  updateSetting: (kind: string, payload: KindSettingPayload) =>
    api.put<Einzel<DocumentSettingsData>>(`/documents/settings/${kind}`, payload),
  updateTexts: (kind: string, blocks: { language: string; formOfAddress?: 'FORMAL' | 'INFORMAL'; slot: string; text: string }[]) =>
    api.put<Einzel<DocumentSettingsData>>(`/documents/settings/${kind}/texts`, { blocks }),

  // ── Layout-Konfigurator (11.3) ──
  layouts: () => api.get<Einzel<DocumentLayoutRow[]>>('/documents/layouts'),
  createLayout: (payload: { name: string; config?: DocumentLayoutConfig; isDefault?: boolean }) =>
    api.post<Einzel<DocumentLayoutRow>>('/documents/layouts', payload),
  updateLayout: (id: string, payload: { name?: string; config?: DocumentLayoutConfig; isDefault?: boolean }) =>
    api.put<Einzel<DocumentLayoutRow>>(`/documents/layouts/${id}`, payload),
  deleteLayout: (id: string) => api.delete<{ message: string }>(`/documents/layouts/${id}`),
  previewLayout: (payload: { config: DocumentLayoutConfig; guides?: boolean; letterhead?: boolean }) =>
    api.post<ArrayBuffer>('/documents/layouts/preview', payload, { responseType: 'arraybuffer' }),

  // ── Auswahlfelder: Bezug, nicht Verwaltung ──
  partners: (q: string, role?: string) => api.get<Einzel<PartnerRef[]>>('/documents/lookup/partners', { params: { q, role } }),
  contacts: (clientId: string) => api.get<Einzel<ContactRef[]>>(`/documents/lookup/partners/${clientId}/contacts`),
  /** N-17: Anschrift beim Wechsel sofort — `contactPersonId` leer = Standard, `none` = keiner. */
  partnerSnapshot: (clientId: string, params: { kind: string; contactPersonId?: string; language?: string }) =>
    api.get<Einzel<PartnerSnapshot>>(`/documents/lookup/partners/${clientId}/snapshot`, { params }),
  /**
   * `clientId` ist der Partner des Belegs: im Einkauf nur dessen Preis und Artikelnummer (Phase G),
   * im Verkauf dessen Kundenpreis. `date` ist das Belegdatum — der Stichtag der Kundenpreise (AP-28 N-18).
   */
  articles: (q: string, role?: string, clientId?: string | null, date?: string | null) =>
    api.get<Einzel<ArticleRef[]>>('/documents/lookup/articles', {
      params: { q, role, ...(clientId && { clientId }), ...(date && { date }) },
    }),
  /** Nachtrag 4: Adressen eines Partners und die eigene Firmenanschrift — für Liefer- und Zustelladresse. */
  partnerAddresses: (clientId: string) => api.get<Einzel<PartnerAddressRef[]>>(`/documents/lookup/partners/${clientId}/addresses`),
  ownAddress: () => api.get<Einzel<{ lines: string[] }>>('/documents/lookup/own-address'),
  /** Nachtrag 3: Einheiten aus dem Artikelstamm und Sachbearbeiter. */
  units: () => api.get<Einzel<UnitRef[]>>('/documents/lookup/units'),
  clerks: () => api.get<Einzel<ClerkRef[]>>('/documents/lookup/users'),
  projects: (q: string) => api.get<Einzel<ProjectRef[]> & { available: boolean }>('/documents/lookup/projects', { params: { q } }),
  projectPositions: (projectId: string) =>
    api.get<Einzel<ProjectPositionRef[]> & { available: boolean }>(`/documents/lookup/projects/${projectId}/positions`),
}

// ── AP-39 10.2: Antwort des Lieferanten an Preisanfrage und Bestellung ───────

/** `OFFER` → `offer`: der Pfadteil der Antwortart. */
const art = (kind: DocumentResponseKind) => kind.toLowerCase()

/** Felder einer Antwortzeile, wie sie die Maske feldweise sendet. */
export type ResponseLinePatch = Partial<
  Pick<DocumentLineResponseView, 'unitPrice' | 'priceUnit' | 'discountPercent' | 'unitCode' | 'quantity' | 'deliveryAt' | 'leadTimeDays' | 'notOffered' | 'alternative' | 'note' | 'label' | 'articleNumber'>
>

export interface EarlierResponse {
  id: string
  isCurrent: boolean
  externalNumber: string | null
  externalDate: string | null
  validUntil: string | null
  note: string | null
  createdAt: string
  lines: DocumentLineResponseView[]
}

export const responsesApi = {
  url: (id: string, kind: DocumentResponseKind) => `/documents/${id}/responses/${art(kind)}`,
  get: (id: string, kind: DocumentResponseKind) => api.get<Einzel<DocumentResponseView>>(responsesApi.url(id, kind)).then((r) => r.data.data),
  earlier: (id: string, kind: DocumentResponseKind, rid: string) => api.get<Einzel<EarlierResponse>>(`${responsesApi.url(id, kind)}/earlier/${rid}`).then((r) => r.data.data),
  head: (id: string, kind: DocumentResponseKind, patch: Record<string, unknown>, stand: string | undefined) =>
    api.patch<Einzel<DocumentResponseHead | null>>(responsesApi.url(id, kind), patch, mitStand(stand)).then((r) => r.data),
  line: (id: string, kind: DocumentResponseKind, lineId: string, patch: Record<string, unknown>, stand: string | undefined) =>
    api.patch<Einzel<DocumentLineResponseView | null>>(`${responsesApi.url(id, kind)}/lines/${lineId}`, patch, mitStand(stand)).then((r) => r.data),
  extra: (id: string, kind: DocumentResponseKind, rid: string, patch: Record<string, unknown>, stand: string | undefined) =>
    api.patch<Einzel<DocumentLineResponseView | null>>(`${responsesApi.url(id, kind)}/extra/${rid}`, patch, mitStand(stand)).then((r) => r.data),
  addExtra: (id: string, kind: DocumentResponseKind, payload: ResponseLinePatch & { label: string }) =>
    api.post<Einzel<DocumentLineResponseView | null>>(`${responsesApi.url(id, kind)}/extra`, payload).then((r) => r.data.data),
  removeExtra: (id: string, kind: DocumentResponseKind, rid: string) => api.delete<Einzel<DocumentResponseView>>(`${responsesApi.url(id, kind)}/extra/${rid}`).then((r) => r.data.data),
  confirm: (id: string, kind: DocumentResponseKind, responseLineIds: string[], head: boolean) =>
    api.post<Einzel<DocumentResponseView>>(`${responsesApi.url(id, kind)}/confirm`, { responseLineIds, head }).then((r) => r.data.data),
  discard: (id: string, kind: DocumentResponseKind, responseLineIds: string[], head: boolean) =>
    api.post<Einzel<DocumentResponseView>>(`${responsesApi.url(id, kind)}/discard`, { responseLineIds, head }).then((r) => r.data.data),
  startNew: (id: string, kind: DocumentResponseKind) => api.post<Einzel<DocumentResponseView>>(`${responsesApi.url(id, kind)}/new`).then((r) => r.data.data),
  /** AP-39 11.1 — „wie bestellt": die genannten Zeilen (ohne Angabe alle noch nicht bestätigten) mit den Werten der Bestellung bestätigen. */
  asOrdered: (id: string, lineIds?: string[]) => api.post<Einzel<DocumentResponseView>>(`${responsesApi.url(id, 'CONFIRMATION')}/as-ordered`, { lineIds }).then((r) => r.data.data),
}
